import React, { useState, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  Background,
  Node,
  Edge,
  MarkerType,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  Position,
  Handle,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Card, CardContent, Typography, Box, Chip, Divider, Button, useTheme, Tooltip } from '@mui/material';
import { PulpPaperRoute } from '../../types';
import KPDiagram from '../pid/KPDiagram';
import MechanicalPulpDiagram from '../pid/MechanicalPulpDiagram';
import ChemimechanicalDiagram from '../pid/ChemimechanicalDiagram';
import DeinkedPulpDiagram from '../pid/DeinkedPulpDiagram';
import LinerboardDiagram from '../pid/LinerboardDiagram';
import CulturalPaperDiagram from '../pid/CulturalPaperDiagram';
import WhiteCardboardDiagram from '../pid/WhiteCardboardDiagram';
import TissuePaperDiagram from '../pid/TissuePaperDiagram';

interface PulpPaperFlowDiagramProps {
  route: PulpPaperRoute;
  onShowDetail?: () => void;
}

const CustomNode: React.FC<{
  data: { label: string; nodeType: 'feedstock' | 'process' | 'product' };
  isDarkMode: boolean;
  isActive?: boolean;
}> = ({ data, isDarkMode, isActive }) => {
  const getNodeStyle = () => {
    const baseStyle = {
      padding: '12px 16px',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: 500,
      minWidth: '160px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      position: 'relative' as const,
    };

    switch (data.nodeType) {
      case 'feedstock':
        return {
          ...baseStyle,
          background: isDarkMode ? '#1e3a5f' : '#e3f2fd',
          border: `2px solid ${isDarkMode ? '#4fc3f7' : '#2196f3'}`,
          color: isDarkMode ? '#ffffff' : '#000000',
        };
      case 'process':
        return {
          ...baseStyle,
          background: isActive
            ? (isDarkMode ? '#4a3728' : '#fff3e0')
            : (isDarkMode ? '#2a2a2a' : '#f5f5f5'),
          border: isActive
            ? `3px solid ${isDarkMode ? '#ffb74d' : '#ff9800'}`
            : `2px solid ${isDarkMode ? '#666666' : '#9e9e9e'}`,
          color: isDarkMode ? '#ffffff' : '#000000',
          cursor: 'pointer',
        };
      case 'product':
        return {
          ...baseStyle,
          background: isDarkMode ? '#1b5e20' : '#e8f5e9',
          border: `2px solid ${isDarkMode ? '#66bb6a' : '#4caf50'}`,
          color: isDarkMode ? '#ffffff' : '#000000',
        };
      default:
        return baseStyle;
    }
  };

  return (
    <div style={getNodeStyle()}>
      {data.nodeType === 'feedstock' && <Handle type="source" position={Position.Right} />}
      {data.nodeType === 'process' && (
        <>
          <Handle type="target" position={Position.Left} />
          <Handle type="source" position={Position.Right} />
        </>
      )}
      {data.nodeType === 'product' && <Handle type="target" position={Position.Left} />}
      
      <span style={{ flex: 1, textAlign: 'center' }}>{data.label}</span>
    </div>
  );
};

const PulpPaperFlowDiagramInner: React.FC<PulpPaperFlowDiagramProps> = ({ route, onShowDetail }) => {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [expanded, setExpanded] = useState(false);
  const isLongFlow = route.processSteps.length > 5;

  const rfInstance = useReactFlow();

  useEffect(() => {
    const timer = setTimeout(() => {
      rfInstance.fitView({ padding: 0.15, duration: 300 });
    }, 60);
    return () => clearTimeout(timer);
  }, [route, rfInstance, expanded]);

  useEffect(() => {
    setExpanded(false);
  }, [route.id]);

  const nodeTypes = useMemo(() => ({
    custom: (props: any) => (
      <CustomNode
        {...props}
        isDarkMode={isDarkMode}
        isActive={activeStep !== null && props.id === `step-${activeStep}`}
      />
    ),
  }), [isDarkMode, activeStep]);

  useEffect(() => {
    const verticalSpacing = 90;
    const horizontalSpacing = 240;
    const startX = 0;
    const centerY = 150;

    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];

    route.feedstock.forEach((item, index) => {
      const yOffset = (index - (route.feedstock.length - 1) / 2) * verticalSpacing;
      newNodes.push({
        id: `feedstock-${index}`,
        type: 'custom',
        position: { x: startX, y: centerY + yOffset },
        data: {
          label: item,
          nodeType: 'feedstock',
        },
      });
    });

    route.processSteps.forEach((step, index) => {
      newNodes.push({
        id: `step-${index}`,
        type: 'custom',
        position: { x: startX + (index + 1) * horizontalSpacing, y: centerY },
        data: {
          label: step,
          nodeType: 'process',
        },
      });

      if (index === 0) {
        route.feedstock.forEach((_, feedIndex) => {
          newEdges.push({
            id: `edge-feed-${feedIndex}-step-${index}`,
            source: `feedstock-${feedIndex}`,
            target: `step-${index}`,
            type: 'smoothstep',
            animated: true,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: isDarkMode ? '#66bb6a' : '#2e7d32',
            },
            style: {
              stroke: isDarkMode ? '#66bb6a' : '#2e7d32',
              strokeWidth: 2,
            },
          });
        });
      } else {
        newEdges.push({
          id: `edge-step-${index - 1}-step-${index}`,
          source: `step-${index - 1}`,
          target: `step-${index}`,
          type: 'smoothstep',
          animated: activeStep !== null && (activeStep === index || activeStep === index - 1),
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isDarkMode ? '#66bb6a' : '#2e7d32',
          },
          style: {
            stroke: isDarkMode ? '#66bb6a' : '#2e7d32',
            strokeWidth: activeStep !== null && (activeStep === index || activeStep === index - 1) ? 3 : 2,
          },
        });
      }
    });

    route.products.forEach((item, index) => {
      const yOffset = (index - (route.products.length - 1) / 2) * verticalSpacing;
      newNodes.push({
        id: `product-${index}`,
        type: 'custom',
        position: { x: startX + (route.processSteps.length + 1) * horizontalSpacing, y: centerY + yOffset },
        data: {
          label: item,
          nodeType: 'product',
        },
      });

      newEdges.push({
        id: `edge-last-step-product-${index}`,
        source: `step-${route.processSteps.length - 1}`,
        target: `product-${index}`,
        type: 'smoothstep',
        animated: true,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isDarkMode ? '#66bb6a' : '#2e7d32',
        },
        style: {
          stroke: isDarkMode ? '#66bb6a' : '#2e7d32',
          strokeWidth: 2,
        },
      });
    });

    setNodes(newNodes);
    setEdges(newEdges);
  }, [route, activeStep, setNodes, setEdges, isDarkMode]);

  const onNodeClick = (_event: React.MouseEvent, node: Node) => {
    if (node.id.startsWith('step-')) {
      const stepIndex = parseInt(node.id.split('-')[1], 10);
      setActiveStep(activeStep === stepIndex ? null : stepIndex);
    }
  };

  return (
    <Card sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
    }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="h6"
            sx={{ color: isDarkMode ? '#66bb6a' : '#2e7d32', fontWeight: 600 }}
          >
            {route.name} - 工艺流程
          </Typography>
          {onShowDetail && (
            <Tooltip title="查看工艺详情">
              <Button onClick={onShowDetail} size="small" sx={{ minWidth: 0, px: 1.5 }}>详情</Button>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ mb: 2, borderColor: isDarkMode ? '#444444' : '#e0e0e0' }} />

        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`原料: ${route.feedstock.length}种`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`步骤: ${route.processSteps.length}步`}
            size="small"
            color="secondary"
            variant="outlined"
          />
          <Chip
            label={`产品: ${route.products.length}种`}
            size="small"
            color="success"
            variant="outlined"
          />
        </Box>

        {isLongFlow && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              流程较长，已折叠为概览显示
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setExpanded(!expanded)}>
              {expanded ? '收起' : '展开大图'}
            </Button>
          </Box>
        )}
        <Box sx={{ height: expanded ? 620 : 360, border: '1px solid', borderColor: isDarkMode ? '#444444' : '#e0e0e0', borderRadius: 1 }}>
          <ReactFlow
            key={route.id}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            minZoom={0.1}
            attributionPosition="bottom-left"
          >
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          </ReactFlow>
        </Box>

        {activeStep !== null && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: isDarkMode ? '#2a2a2a' : '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: isDarkMode ? '#ffb74d' : '#ff9800' }}>
              当前步骤: {route.processSteps[activeStep]}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              点击其他步骤节点可切换高亮显示
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const pidDiagrams: Record<string, React.FC<{ route: PulpPaperRoute }>> = {
  'chemical-pulp': KPDiagram,
  'mechanical-pulp': MechanicalPulpDiagram,
  'chemimechanical-pulp': ChemimechanicalDiagram,
  'deinked-pulp': DeinkedPulpDiagram,
  linerboard: LinerboardDiagram,
  'cultural-paper': CulturalPaperDiagram,
  'white-cardboard': WhiteCardboardDiagram,
  'tissue-paper': TissuePaperDiagram,
};

const PulpPaperFlowDiagram: React.FC<PulpPaperFlowDiagramProps> = (props) => {
  // 制浆造纸模块各路线的 P&ID 风格详细流程图（独立组件，自带 ReactFlowProvider）
  const PidDiagram = pidDiagrams[props.route.id];
  if (PidDiagram) {
    return <PidDiagram route={props.route} />;
  }
  return (
    <ReactFlowProvider>
      <PulpPaperFlowDiagramInner {...props} />
    </ReactFlowProvider>
  );
};

export default PulpPaperFlowDiagram;
